import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_DELAY_MS = 200;

export type UseDelayedHoverResult = {
  /** Whether the hover state is active */
  isOpen: boolean;
  /** Call this on mouse enter to start the open delay */
  handleMouseEnter: VoidFunction;
  /** Call this on mouse leave to start the close delay */
  handleMouseLeave: VoidFunction;
  /** Call this to close immediately without delay */
  handleClose: VoidFunction;
};

export const useDelayedHover = (): UseDelayedHoverResult => {
  const [isOpen, setIsOpen] = useState(false);

  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimeouts = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, [clearTimeouts]);

  const handleMouseEnter = useCallback(() => {
    clearTimeouts();
    openTimeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, DEFAULT_DELAY_MS);
  }, [clearTimeouts]);

  const handleMouseLeave = useCallback(() => {
    clearTimeouts();
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, DEFAULT_DELAY_MS);
  }, [clearTimeouts]);

  const handleClose = useCallback(() => {
    clearTimeouts();
    setIsOpen(false);
  }, [clearTimeouts]);

  return {
    isOpen,
    handleMouseEnter,
    handleMouseLeave,
    handleClose,
  };
};
